import { selectAuth } from '@brands/store/selectors/auth';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Meta } from '../services/communication/types/Meta';
import { IListCAParams, listAllCareAdvisors } from '../services/identity/listAllCA';
import { UserProfile } from '../services/identity/types/UserProfile';
import { useAppSelector } from './useReduxHook';

interface IUseConversations {
  careAdvisors: UserProfile[];
  reloadAll: () => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  dataLoading: boolean;
  loadError: string | null;
}

export default function useCareAdvisor(
  params: Pick<IListCAParams, 'sort_by' | 'sort_direction' | 'case_id' | 'limit'>,
  shouldLoadAllCases = false,
): IUseConversations {
  const { userInfo } = useAppSelector(selectAuth);
  const [careAdvisors, setCareAdvisors] = useState<UserProfile[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [firstPageMeta, setFirstPageMeta] = useState<Meta | null>(null);
  const [lastPageMeta, setLastPageMeta] = useState<Meta | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const sortCareAdvisors = useCallback(
    (careAdvisorsToSort: UserProfile[]): UserProfile[] => {
      const filterCAsWithoutName = careAdvisorsToSort.filter((careAdvisor) => careAdvisor.full_name);
      if (userInfo.role.name !== 'CareAdvisor' || !userInfo.full_name) {
        return filterCAsWithoutName;
      }
      const currentCareAdvisorIndex = filterCAsWithoutName.findIndex((careAdvisor) => careAdvisor.id === userInfo.id);
      if (currentCareAdvisorIndex === -1) {
        return filterCAsWithoutName;
      }
      const careAdvisorsToSortCopy = [...filterCAsWithoutName];
      const currentCareAdvisorCopy = careAdvisorsToSortCopy.splice(currentCareAdvisorIndex, 1)[0];
      return [currentCareAdvisorCopy, ...careAdvisorsToSortCopy];
    },
    [userInfo.role.name, userInfo.full_name, userInfo.id],
  );

  const reloadAll = useCallback(async (): Promise<void> => {
    const version = ++requestVersion.current;
    setDataLoading(true);
    setLoadError(null);
    setCareAdvisors([]);
    setFirstPageMeta(null);
    setLastPageMeta(null);
    try {
      const careAdvisorList = await listAllCareAdvisors(params, userInfo.role.name);
      if (version !== requestVersion.current) return;
      setCareAdvisors(sortCareAdvisors(careAdvisorList.nodes));
      setFirstPageMeta(careAdvisorList.meta);
      setLastPageMeta(careAdvisorList.meta);
    } catch {
      if (version === requestVersion.current) {
        setLoadError('Unable to load care advisors. Please try again.');
      }
    } finally {
      if (version === requestVersion.current) setDataLoading(false);
    }
  }, [params, userInfo.role.name, sortCareAdvisors]);

  const loadPage = async (direction: 'next' | 'previous'): Promise<void> => {
    const pageMeta = direction === 'next' ? lastPageMeta : firstPageMeta;
    if (dataLoading || !pageMeta || !(direction === 'next' ? pageMeta.has_next_page : pageMeta.has_previous_page)) {
      return;
    }
    const version = requestVersion.current;
    try {
      const { meta, nodes } = await listAllCareAdvisors(
        {
          ...params,
          cursor: direction === 'next' ? pageMeta.end_cursor : pageMeta.start_cursor,
          cursor_direction: direction,
        },
        userInfo.role.name,
      );
      if (version !== requestVersion.current) return;
      setLoadError(null);
      const sorted = sortCareAdvisors(nodes);
      if (direction === 'next') {
        setLastPageMeta(meta);
        if (!firstPageMeta) setFirstPageMeta(meta);
        setCareAdvisors((previous) => uniqBy([...previous, ...sorted], 'id'));
      } else {
        setFirstPageMeta(meta);
        if (!lastPageMeta) setLastPageMeta(meta);
        setCareAdvisors((previous) => uniqBy([...sorted, ...previous], 'id'));
      }
    } catch {
      if (version === requestVersion.current) {
        setLoadError('Unable to load care advisors. Please try again.');
      }
    }
  };

  const loadNextPage = (): Promise<void> => loadPage('next');
  const loadPreviousPage = (): Promise<void> => loadPage('previous');

  useEffect(() => {
    if (shouldLoadAllCases) {
      reloadAll();
    }
    return () => {
      requestVersion.current += 1;
    };
  }, [shouldLoadAllCases, reloadAll]);

  return {
    careAdvisors,
    reloadAll,
    loadNextPage,
    loadPreviousPage,
    dataLoading,
    loadError,
  };
}
